// @ts-nocheck
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import * as cssLinter from "css";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        pageNodeId: getCurrentPage(state).nodeId,
    };
};

interface SectionModel {
    fields: {
        heading: string;
        backgroundImage: string;
        destination: LinkFieldValue;
    };
}

const enum fields {
    sections = "sections",
    buttonPadding = "buttonPadding",
    buttonSpacing = "buttonSpacing",
    useLeftRightSiteMargins = "useLeftRightSiteMargins",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonPadding]: number;
        [fields.buttonSpacing]: number;
        [fields.useLeftRightSiteMargins]: number;
        [fields.customCSS]: string;
        [fields.sections]: SectionModel[];
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface LinkBannerStyles {
    wrapper?: InjectableCss;
    rightAlignWrapper?: InjectableCss;
    buttonStyle?: ButtonPresentationProps;
    activeButtonStyle?: ButtonPresentationProps;
}

const buttonStyleCss = css`
    padding: ${pxToRem(10)};
    border: none;
    &:last-child {
        margin-bottom: 0;
    }
    ${({ theme }) => {
        return breakpointMediaQueries(
            theme,
            [
                css`
                    width: 100%;
                `,
                css`
                    width: ${pxToRem(350)};
                `,
                null,
                null,
                null,
            ],
            "min",
        );
    }}
`;
const buttonStyle: ButtonPresentationProps = {
    variant: "primary",
    sizeVariant: "large",
    typographyProps: {
        size: 23,
    },
    hoverStyle: {
        background: "rgba(233, 42, 40, 0.62)",
    },
    css: css`
        ${buttonStyleCss}
        background: rgba(0, 0, 0, 0.6);
    `,
};

export const linkBannerStyles: LinkBannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            background-repeat: no-repeat;
            background-size: cover;
            &::after {
                content: "";
                width: 100%;
                background: linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1));
                z-index: 0;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                display: none;
                            `,
                            css`
                                display: inherit;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "min",
                    );
                }}
            }
        `,
    },
    rightAlignWrapper: {
        css: css`
            z-index: 1;
            min-width: ${pxToRem(360)};
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            max-width: revert;
                        `,
                        css`
                            width: 25%;
                            max-width: ${pxToRem(360)};
                        `,
                        null,
                        null,
                        null,
                    ],
                    "min",
                );
            }}
        `,
    },
    buttonStyle,
    activeButtonStyle: {
        ...buttonStyle,
        css: css`
            ${buttonStyleCss}
            background: rgba(233, 42, 40, 0.62);
        `,
    },
};

const styles = linkBannerStyles;

const LinkBanner: FC<Props> = ({
    fields: { sections, buttonPadding, buttonSpacing, useLeftRightSiteMargins, customCSS },
    pageNodeId,
}) => {
    const [backgroundStyle, setBackgroundStyle] = useState("");

    useEffect(() => {
        const bgImgItem = sections.filter(sectionRow => sectionRow.fields.destination.value === pageNodeId);
        const bgImgUrl = bgImgItem.pop()?.fields.backgroundImage;
        if (bgImgUrl) {
            setBackgroundStyle(`
                background-image: url("${bgImgUrl}");
            `);
        }
    }, [pageNodeId]);

    const customCssWrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyle}
            ${customCSS}
        `,
    };
    const wrapperStyles = {
        css: css`
            ${useLeftRightSiteMargins ? siteWidthStyles() : null}
            padding-top: ${pxToRem(buttonSpacing)};
            padding-bottom: ${pxToRem(buttonSpacing)};
        `,
    };

    return (
        <StyledWrapper {...customCssWrapperStyles}>
            <StyledWrapper {...wrapperStyles} className="wrapper">
                <StyledWrapper {...styles.rightAlignWrapper} className="button-wrapper">
                    {sections?.map(sectionRow => {
                        const destinationLinkField = sectionRow.fields.destination;
                        return (
                            <ButtonView
                                key={`${destinationLinkField}-${newGuid()}`}
                                currentPageNode={pageNodeId}
                                destinationLink={destinationLinkField}
                                buttonTitle={sectionRow.fields.heading}
                                buttonPadding={buttonPadding}
                                buttonSpacing={buttonSpacing}
                            />
                        );
                    })}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

type ButtonViewProps = {
    currentPageNode: string;
    destinationLink: LinkFieldValue;
    buttonTitle: string;
    buttonPadding: number;
    buttonSpacing: number;
};

const ButtonView = ({
    currentPageNode,
    destinationLink,
    buttonTitle,
    buttonPadding,
    buttonSpacing,
}: ButtonViewProps) => {
    const history = useHistory();
    const buttonUrl = useGetLink(destinationLink).url;
    const [activeButton, setActiveButton] = useState(false);

    useEffect(() => {
        if (currentPageNode === destinationLink.value) {
            setActiveButton(true);
        }
    }, [currentPageNode]);

    const onClickHandler = () => {
        if (buttonUrl) {
            history.push(buttonUrl);
        }
    };

    const buttonCss = css`
        ${buttonPadding
            ? breakpointValues(
                  [pxToRem(buttonPadding / 2), pxToRem(buttonPadding), null, null, null],
                  ["padding-top", "padding-bottom"],
              )
            : null};
        ${buttonSpacing ? `margin-bottom: ${pxToRem(buttonSpacing / 2)};` : null}
    `;

    const activeButtonStyle = {
        css: css`
            ${styles.activeButtonStyle?.css}
            ${buttonCss};
        `,
    };

    const buttonStyle = {
        css: css`
            ${styles.buttonStyle?.css}
            ${buttonCss};
        `,
    };

    return (
        <Button onClick={onClickHandler} {...(activeButton ? activeButtonStyle : buttonStyle)} className="button">
            {buttonTitle}
        </Button>
    );
};

const defaultCustomCss = `& {
}

.wrapper {
}

.button-wrapper {
}

.button {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(LinkBanner),
    definition: {
        group: "Basic",
        icon: "sidebar",
        fieldDefinitions: [
            {
                name: fields.sections,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.heading;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "heading",
                        displayName: "Heading",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        placeholder: "Heading",
                        isRequired: true,
                    },
                    {
                        name: "backgroundImage",
                        displayName: "Background Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "destination",
                        displayName: "Destination",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                        allowUrls: () => false,
                        allowCategories: () => false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.buttonPadding,
                displayName: "Button Vertical Padding",
                tooltip: "In pixels, will be smaller on mobile.",
                editorTemplate: "IntegerField",
                defaultValue: 30,
                min: 10,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.buttonSpacing,
                displayName: "Button Spacing",
                tooltip: "In pixels",
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.useLeftRightSiteMargins,
                displayName: "Use Site margins",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
